import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

/**
 * RouteGuard wraps protected routes and redirects to home with a toast
 * when the user navigates to a path not in their accessibleRoutes.
 *
 * Behavior:
 * - Skips guard if no user (auth middleware handles that)
 * - Skips guard if accessibleRoutes not yet loaded (avoids false redirect during initial fetch)
 * - Public paths (/login, /auth/callback, /) are always allowed
 * - Rutas de contenido informativo de libre acceso (p. ej. /guia-desarrollo)
 *   siempre se permiten: son material de habilitación sin datos sensibles.
 * - On denied access → navigate to "/" + show toast for 5 seconds
 */
export function RouteGuard({ children }) {
  const { user, accessibleRoutes, hasAccess } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lastDeniedPath = useRef(null);

  useEffect(() => {
    // Skip guard if no user (Protected wrapper handles unauthenticated)
    if (!user) return;

    // Skip guard until permissions have loaded
    if (accessibleRoutes.length === 0) return;

    const currentPath = location.pathname;

    // Public routes are always accessible (auth + contenido informativo abierto)
    const publicPaths = ['/login', '/auth/callback', '/', '/guia-desarrollo'];
    if (publicPaths.includes(currentPath)) return;

    // Check if user has access to this path
    if (!hasAccess(currentPath)) {
      // Avoid duplicate toasts for same denied path (e.g. on re-renders)
      if (lastDeniedPath.current !== currentPath) {
        lastDeniedPath.current = currentPath;
        toast.error('No tiene acceso a la sección solicitada', {
          duration: 5000,
        });
      }
      navigate('/', { replace: true });
    } else {
      // Reset denied path tracker on successful navigation
      lastDeniedPath.current = null;
    }
  }, [location.pathname, user, accessibleRoutes, hasAccess, navigate]);

  return children;
}
